import { Flex } from 'antd';
import { ProgressHeader } from 'pages/auth/registration/components/ProgressHeader';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { GridContainer } from 'shared/ui';
import CodeInput, { CodeInputRef } from 'shared/ui/CodeInput/CodeInput';
import { useConfirmPhoneCodeMutation, useSendPhoneCodeQuery } from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks';
import { ApiError } from 'shared/model/types';
import { ROUTES } from 'shared/const/routes';
import { useLazyGetUserInfoQuery, useLazyGetUserQuery, useSaveGtagMutation } from 'entities/user';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { GTAG_ID } from 'shared/const/app';
import classes from './ConfirmPhone.module.css';

export const ConfirmPhone = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const phoneFromParams = params.get('phone');
  const codeRef = useRef<CodeInputRef>(null);
  const { formatMessage: t } = useIntl();
  const user = useSelector(getUserMeta);
  const [fetchUser] = useLazyGetUserQuery();
  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  
  const phone = phoneFromParams || user.phone;
  const email = params.get('email') || user.email;
  const isEmailVerified = user.verified;
  const [isCodeConfirmed, setIsCodeConfirmed] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  
  const { refetch: refetchCodeSend } = useSendPhoneCodeQuery(undefined, {
    skip: !isEmailVerified,
  });
  const [confirmPhoneCode] = useConfirmPhoneCodeMutation();
  const [saveGtag] = useSaveGtagMutation();
  
  const dispatch = useDispatch();
  const { showSuccess, showError } = useMessage();

  useEffect(() => {
    if (user.id === 0 || (user.id !== 0 && !phone)) {
      setIsLoadingUser(true);
      Promise.all([
        fetchUser(),
        fetchUserInfo(),
      ]).catch((error: ApiError) => {
        console.error('Error fetching user data:', error);
      }).finally(() => {
        setIsLoadingUser(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoadingUser && !isEmailVerified && user.id !== 0 && !isCodeConfirmed && !user.verified) {
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone}`
      }, { replace: true });
    }
  }, [isEmailVerified, user.id, user.verified, email, phone, navigate, isCodeConfirmed, isLoadingUser]);

  const handleCodeConfirm = (code: string) => {
    if (!user.verified && user.id !== 0) {
      showError(t({ id: 'common.message.codeInvalid' }));
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone}`
      });
      return;
    }

    dispatch(setActivity(true));
    confirmPhoneCode({ code })
      .unwrap()
      .then(async () => {
        setIsCodeConfirmed(true);
        gtag('get', GTAG_ID, 'client_id', (clientID) => {
          saveGtag({ clientId: clientID as string, eventName: 'web_phone_verification_completed', gtag: GTAG_ID });
          gtag('event', 'web_phone_verification_completed', {
            phone_verification: true
          });
        });
        codeRef.current?.success();
        // Сначала переходим на dashboard, потом обновляем данные
        showSuccess(t({ id: 'loginPage.welcome' }));
        navigate(ROUTES.DASHBOARD.path);
        // Обновляем данные после навигации
        await Promise.all([
          fetchUserInfo(),
          fetchUser(),
        ]);
      })
      .catch((error: ApiError) => {
        console.log(error);
        codeRef.current?.clear();
        codeRef.current?.error(t({ id: 'common.message.codeInvalid' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  const handleResend = () => {
    if (!user.verified && user.id !== 0) {
      showError(t({ id: 'common.message.codeInvalid' }));
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone}`
      });
      return;
    }
    refetchCodeSend();
  }

  if (isLoadingUser && !phone) {
    return null;
  }

  return (
    <GridContainer>
      <div className={classes.container}>
        <Flex vertical gap={40} style={{ width: '100%' }}>
          <ProgressHeader sectionIndex={5} last={6} title={t({ id: 'profile.settings.phone.confirm' })} onBack={() => navigate(ROUTES.LOGIN.path, { replace: true })} description={t({ id: 'profile.settings.phone.codeSent' }, { phone: phone || '' })} />
          <Flex justify='center' style={{ width: '100%' }}>
            <CodeInput timeout={30} ref={codeRef} isPhone onResend={handleResend} onEnter={handleCodeConfirm} />
          </Flex>
        </Flex>
      </div>
    </GridContainer>
  )
}